import ICON__trash_solid from '@/assets/icons/font_awesome/trash-solid.svg?react';
import NotifierBorder from '@/components/ui/notifier/notifier-border';
import { UI_PRIMITIVE__button_with_icon_animated } from '@/components/ui/UI_PRIMITIVE__button_with_icon_animated';
import { UI_PRIMITIVE__progress_bar } from '@/components/ui/UI_PRIMITIVE__progress_bar';
import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_generation, use_slice_ui, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import i18n_page_generation from '@/locales/keys/i18n_page_generation.json';
import { PLACEHOLDER_BUTTON__bar_10_5 } from '@/pages/_base/buttons/PLACEHOLDER_BUTTON__bar_10_5';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD__list_token } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_token';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PROPS {
  field_list: FIELD__list_token;
}

export function G_BUTTON__prompt_delete_all_tokens({ field_list }: PROPS) {
  /*
   * STORE
   */
  const { t } = useTranslation([ENUM__i18n_namespace.PAGE_GENERATION, ENUM__i18n_namespace.BASE_UI]);

  const { toast, dismiss } = useToast();

  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { generation_current } = use_slice_generation(['generation_current']);

  /*
   * STATE
   */

  const component_id = `${ENUM__component_type.PAGE_GENERATION_BUTTON_DELETE_ALL_TOKENS}_${field_list.meta.field_id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_GENERATION);

  /*
   * STATE
   */
  const [is_clicked, set_is_clicked] = useState(false);
  const [is_loading, set_is_loading] = useState(false);

  const ref_current_toast_id = useRef('');

  const tooltip_title = t(i18n_page_generation.sidebar_user_input.button.delete_all_tokens.tooltip);

  /*
   * EVENTS
   */

  function onTimerComplete() {
    set_is_clicked(false);

    dismiss(ref_current_toast_id.current);
    ref_current_toast_id.current = '';
  }

  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();

    if (is_clicked || !user_settings.confirm_action_delete_all_tokens) {
      onClickSecondTime();
    } else {
      onClickFirstTime();
    }
  }

  async function onClickFirstTime() {
    set_is_clicked(true);

    let description = (
      <UI_PRIMITIVE__progress_bar
        timeout_ms={CONSTANTS_TIME__client.TOAST_DURATION_DELETE_ALL_TOKENS}
        text={t(i18n_base_ui.toasts.delete_all_tokens.description)}
      />
    );

    const toast_data = toast({
      title: t(i18n_base_ui.toasts.delete_all_tokens.title),
      duration: CONSTANTS_TIME__client.TOAST_DURATION_DELETE_ALL_TOKENS,
      description,
      onDismiss: onTimerComplete,
    });
    ref_current_toast_id.current = toast_data.id;
  }

  async function onClickSecondTime() {
    onTimerComplete();

    let field_list_new = field_list.clone();

    // todo: need remove edges from field and from children too
    field_list_new.items = field_list_new.items_non_ready_to_delete;
    field_list_new.field_name_and_path__fill_for_children();

    /*
     *
     */
    set_is_loading(true);

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_list_new);

    set_is_loading(false);

    /*
     *
     */
    await notifier.send_action();
  }

  /*
   * METHODS
   */

  function is_disabled() {
    return g_general__ui_disabled || field_list.items_ready_to_delete.is_empty() || is_loading;
  }

  /*
   * HTML
   */

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_BUTTON__bar_10_5 />;

  return (
    <UI_PRIMITIVE__tooltip {...{ tooltip_title }}>
      <UI_PRIMITIVE__button_with_icon_animated
        variant="no-border-on-bar"
        size={'rect-10-5'}
        Icon={ICON__trash_solid}
        renderDelay={CONSTANTS_TIME__client.TIMEOUT_DELAY_BEFORE_FIRST_ANIMATION_RENDER}
        iconClassNameBase={`!size-3 ${is_clicked ? '!text-danger' : '!text-component-item-foreground'}`}
        iconClassNameHovered={`${is_clicked ? '!text-danger' : '!text-bar-foreground'}`}
        disabled={is_disabled()}
        {...{ onClick }}
      >
        <NotifierBorder {...{ notifier }} />
      </UI_PRIMITIVE__button_with_icon_animated>
    </UI_PRIMITIVE__tooltip>
  );
}
